import { AxiosInstance, AxiosResponse, CancelToken } from 'axios'
import store from '@/store'
import IApiBaseService from '@/services/interfaces/IApiBase.service'
import { AxiosClient } from '@/axios'
import { IResponse } from '@/types'
import { markAsHandledError, pushSnackbarMessage } from '@/app/errors'
import qs from 'qs'
import FileSaver from 'file-saver'
import { LocalStorageService } from '@/localStorageService'

export abstract class ApiBaseService<TEntity, TAllResponse = TEntity>
  implements IApiBaseService<TEntity, TAllResponse> {

  abstract readonly apiController: string
  protected client: AxiosInstance = AxiosClient

  constructor() {
    this.client.interceptors.request.use(
      (requestConfig) => {
        const token = LocalStorageService.getAccessToken()

        if (token) requestConfig.headers.Authorization = 'Bearer ' + token

        return requestConfig
      },
      async (error) => {
        pushSnackbarMessage(store, error.message)
        return Promise.reject(error)
      },
    )

    this.client.interceptors.response.use(
      (response) => response,
      async error => {
        if (error.response?.status === 401) {
          await store.dispatch('Account/logout')
          throw error
        }
        if (error.response?.data?.responseException) {
          const responseException = markAsHandledError(error.response.data.responseException)
          if (!error.response.data.responseException.validationErrors) {
            pushSnackbarMessage(store, responseException.exceptionMessage)
          }
          throw responseException
        }
        throw error
      })
  }

  async get<TResponse = TEntity>(path: string = '', params?: any): Promise<TResponse> {
    path = this.preparePath(path)
    const response =
      await this.client.get<IResponse<TResponse>>(`${this.apiController}${path}`, { params })
    return this.unwrapResponse(response)
  }

  async getAll(path: string = '', params?: any): Promise<TAllResponse> {
    path = this.preparePath(path)
    const response = await this.client.get<IResponse<TAllResponse>>(`${this.apiController}${path}`, {
      params, paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })

    return this.unwrapResponse<TAllResponse>(response)
  }

  async getAllWithCancelToken(cancelToken: CancelToken, path: string = '', params?: any): Promise<TAllResponse> {
    path = this.preparePath(path)
    const response = await this.client.get<IResponse<TAllResponse>>(`${this.apiController}${path}`, {
      cancelToken,
      params, paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })

    return this.unwrapResponse<TAllResponse>(response)
  }

  async getById(id: number | string, path: string = ''): Promise<TEntity> {
    return this.get(`${id}${path}`)
  }

  async delete(id: number, path: string = ''): Promise<boolean> {
    path = this.preparePath(path)
    const response = await this.client.delete<IResponse<boolean>>(`${this.apiController}/${id}${path}`)
    return this.unwrapResponse(response)
  }

  async post<TResponse = TEntity, TRequest = TEntity>(entity?: TRequest, path: string = ''): Promise<TResponse> {
    path = this.preparePath(path)
    const response = await this.client.post<IResponse<TResponse>>(`${this.apiController}${path}`, entity)
    return this.unwrapResponse<TResponse>(response)
  }

  async download(params: any, path: string = '', filename?: string, preview: boolean = false): Promise<void> {
    path = this.preparePath(path)
    const response = await this.client.post(`${this.apiController}${path}`, params, {
      responseType: 'blob', timeout: 300000,
      onDownloadProgress: (progressEvent) => {
        const percentage = (progressEvent.loaded * 100) / (progressEvent.total ?? progressEvent.loaded)
        store.commit('Downloads/setPercentage', percentage)
      },
    })

    const file = new File([response.data], filename ?? 'documents.zip',
     { type: preview ? 'application/pdf' : response.data.type })

    if (preview) {
      const fileURL = window.URL.createObjectURL(file)
      window.open(fileURL)
    } else {
      FileSaver.saveAs(file)
    }

    store.commit('Downloads/setPercentage', 0)
  }

  async put(entity?: TEntity, path: string = ''): Promise<TEntity> {
    path = this.preparePath(path)
    const response =
      await this.client.put<IResponse<TEntity>>(`${this.apiController}${path}`, entity)
    return this.unwrapResponse(response)
  }

  private unwrapResponse<TResponse = TEntity>(axiosResponse: AxiosResponse<IResponse<TResponse>>): TResponse {
    const response = axiosResponse.data

    if (!('isError' in response)) {
      throw new Error(`Invalid response ${response}`)
    }
    if (!response.isError) {
      return response.result
    } else {
      const responseException = markAsHandledError(response.responseException)

      if (!responseException.validationErrors?.length) {
        pushSnackbarMessage(store, responseException.exceptionMessage)
      }

      throw responseException
    }
  }

  private preparePath(path: string): string {
    return path === '' || path.startsWith('/') ? path : `/${path}`
  }
}
